// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga gizobstacle.cpp: update mode table, Proximity/PosWithinBox,
// SatisfyingTerrainChecks, Activate/ActivateRev/GetOutput/Hit. Authored model
// matrices are sampled through scene-animation.mjs; mission signals stay external.
const vec = value => {
  if (value && !Array.isArray(value) && !ArrayBuffer.isView(value)) value = [value.x, value.y, value.z];
  if (!value || value.length !== 3 || !Array.from(value).every(Number.isFinite)) throw new Error('Invalid obstacle position.');
  return Array.from(value);
};
const distanceSq = (a, b) => a.reduce((sum, v, i) => sum + (v - b[i]) ** 2, 0);

export function obstaclePositionInBox(obstacle, position) {
  const delta = vec(position).map((v, i) => v - obstacle.secondaryPosition[i]);
  const units = (-obstacle.triggerBoxYaw) & 65535;
  const angle = (units >> 1) * 2 * Math.PI / 32768, c = Math.cos(angle), s = Math.sin(angle);
  const x = delta[0] * c + delta[2] * s, z = delta[2] * c - delta[0] * s, half = obstacle.triggerBoxHalfExtents;
  return x >= -half[0] && x <= half[0] && delta[1] >= 0 && delta[1] <= half[1] && z >= -half[2] && z <= half[2];
}

// GameAnimSet_GetCentreAndRadius transforms local sphere centers, shrinks each
// radius by .75, unions center±radius cubes and takes the half-diagonal. The
// native radius is intentionally not scaled by the current model matrix.
export function evaluateObstacleBounds(record, objectSphere = null) {
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
  let count = 0;
  for (const piece of record.pieces ?? []) {
    if (piece.flags & 1) continue;
    const sphere = objectSphere ? objectSphere(piece.name) : piece.binding?.localSphere;
    if (!sphere || !Number.isFinite(sphere.radius) || sphere.radius < 0 || !piece.matrix?.every(Number.isFinite)) return null;
    const c = vec(sphere.center), m = piece.matrix, radius = sphere.radius * 0.75;
    const center = [0, 1, 2].map(axis => c[0] * m[axis] + c[1] * m[4 + axis] + c[2] * m[8 + axis] + m[12 + axis]);
    for (let axis = 0; axis < 3; axis++) {min[axis] = Math.min(min[axis], center[axis] - radius); max[axis] = Math.max(max[axis], center[axis] + radius);}
    count++;
  }
  if (!count) return {position: record.position.slice(), radius: 1, contributingObjects: 0};
  const position = min.map((v, i) => (v + max[i]) / 2), half = min.map((v, i) => (max[i] - v) / 2);
  return {position, radius: Math.hypot(...half), minimum: min, maximum: max, contributingObjects: count};
}

export function createObstacleState(gizmos, {animationAdapter, progress = {}, gizmoFlags = {},
  platformForObject = () => -1, objectSphere = null, excludeActor = null} = {}) {
  if (!Array.isArray(gizmos?.obstacles) || typeof animationAdapter?.resolve !== 'function') throw new Error('Original obstacles and scene animation adapter required.');
  const records = [], byId = new Map(), byName = new Map(), events = [];
  const lookup = (container, key) => container instanceof Map ? container.get(key) : container?.[key];
  function resolve(ref) {
    if (typeof ref === 'string') return byId.get(ref) ?? byName.get(ref.toLowerCase()) ?? null;
    if (ref && ['gizobstacle', 'obstacle'].includes(String(ref.type).toLowerCase())) return byName.get(String(ref.name).toLowerCase()) ?? null;
    return null;
  }
  function emit(type, r, fields = {}) {events.push({type, id: r.id, name: r.name, kind: 'obstacle', ...fields});}
  function put(piece, frame) {piece.frame = frame; piece.matrix = piece.binding.sample(frame); animationAdapter.setMatrix?.(piece.name, piece.matrix);}
  const animated = r => r.pieces.filter(p => p.binding.animationIndex !== undefined);
  const atEnd = r => animated(r).every(p => p.frame === p.endFrame);
  const atStart = r => !atEnd(r) && animated(r).every(p => p.frame === p.startFrame);
  function setVisibility(r, visible) {r.visible = !!visible; for (const p of r.pieces) animationAdapter.setVisible?.(p.name, r.visible && !r.destroyed);}
  function bounds(r) {const result = evaluateObstacleBounds(r, objectSphere); r.boundsUnsupported = !result;
    if (result) {r.evaluatedPosition = result.position; r.radius = result.radius;} return result;}
  function jump(r, end) {r.direction = 0; for (const p of r.pieces) put(p, end ? p.endFrame : p.startFrame); bounds(r);}
  function play(r, direction) {
    if ((direction > 0 && atEnd(r)) || (direction < 0 && atStart(r))) {r.direction = 0; return;}
    r.direction = direction;
  }
  for (const source of gizmos.obstacles) {
    const saved = lookup(progress, source.id) ?? {}, flags = lookup(gizmoFlags, source.id) ?? lookup(gizmoFlags, source.name.toLowerCase()) ?? {};
    const r = {...source, position: vec(source.position), secondaryPosition: vec(source.secondaryPosition),
      enabled: saved.enabled ?? flags.enabled ?? true, visible: saved.visible ?? flags.visible ?? true,
      destroyed: saved.destroyed ?? false, pickupsSpawned: saved.pickupsSpawned ?? false, pieces: [], unsupported: [],
      direction: 0, animationSpeed: 1, proximity: false, aiActive: saved.aiActive ?? false,
      triggerLatched: saved.triggerLatched ?? false, reverseActive: false, externalControl: false, returnTimer: 0, endReleased: false};
    if (!Number.isInteger(r.mode) || r.mode < 0 || r.mode > 7) r.unsupported.push('unknown obstacle mode');
    if (r.authoredState === 2) r.unsupported.push('repeating animation intervals');
    for (const object of source.animationSet?.objects ?? []) {
      if (!object.name) continue;
      const binding = animationAdapter.resolve(object.name);
      if (!binding || binding.unsupported) {r.unsupported.push(`missing original animation: ${object.name}`); continue;}
      const piece = {...object, binding, platformId: platformForObject(object.name)};
      if (![piece.startFrame, piece.endFrame].every(Number.isFinite)) throw new Error('Invalid obstacle animation interval.');
      r.pieces.push(piece); put(piece, saved.complete ? piece.endFrame : piece.startFrame);
    }
    // Source bit2 replaces the authored trigger position by the average of
    // contributing original animation objects at their start frames.
    if (r.configFlags & 2) {
      const parts = r.pieces.filter(p => !(p.flags & 1));
      if (parts.length) r.secondaryPosition = [0, 1, 2].map(axis => parts.reduce((sum, p) => sum + p.matrix[12 + axis], 0) / parts.length);
    }
    setVisibility(r, r.visible);
    bounds(r);
    records.push(r); byId.set(r.id, r); byName.set(r.name.toLowerCase(), r);
  }
  function actorAllowed(r, actor) {
    if (actor.inUse === false || actor.character === false || actor.disabled || actor.doomed || actor.triggerDisabled || actor.excludedContext) return false;
    const player = actor.playerIndex ?? (actor.isPlayer ? 0 : -1), flags = r.configFlags;
    if ((flags & 4) && player < 0) return false;
    if ((flags & 32) && actor.active !== true) return false;
    if ((flags & 1024) && actor.linkedObject == null) return false;
    if ((flags & 16384) && !(actor.characterFlags04 & 4)) return false;
    if ((flags & 8) && !(actor.characterFlags05 & 32)) return false;
    if (excludeActor?.(r, actor)) return false;
    const terrainValid = !!actor.terrainState || !!actor.obstacleTerrainValid;
    const onPlatform = r.pieces.some(p => p.platformId >= 0 && p.platformId === actor.supportingPlatformId);
    if ((r.mode & 0xfb) === 2 && !terrainValid && !actor.obstaclePlatformBypass && !onPlatform) return false;
    const triggerMode = r.triggerMode >= 128 ? r.triggerMode - 256 : r.triggerMode;
    if (triggerMode >= 0 && (!terrainValid || triggerMode !== actor.terrainType)) return false;
    if (triggerMode < 0 && (flags & 16) && (!terrainValid || !onPlatform)) return false;
    return true;
  }
  function proximity(r, {actors = [], cameraPosition = null, cameraMode = -1, triggers = []}) {
    if (triggers.length > 16) throw new Error('Original obstacle trigger capacity exceeded.');
    let closest = r.triggerRadius ** 2, found = false, playerFailures = new Set();
    const box = r.mode === 5 || r.mode === 6;
    function point(position, collision = position) {
      const p = vec(position), d = distanceSq(r.secondaryPosition, p);
      if (d >= closest || (box && !obstaclePositionInBox(r, collision))) return false;
      closest = d; found = true; return true;
    }
    const ordinary = [1, 2, 5, 6].includes(r.mode);
    if (ordinary && (r.mode & 0xfb) === 1 && cameraMode !== -1 && r.triggerMode === 255 &&
        !(r.configFlags & (1 | 4 | 16 | 32 | 1024 | 16384)) && cameraPosition) point(cameraPosition);
    if (ordinary && !(r.configFlags & 1)) for (const trigger of triggers) point(trigger);
    for (const actor of actors) {
      const player = actor.playerIndex ?? (actor.isPlayer ? 0 : -1);
      if (actor.inUse === false || actor.character === false || actor.disabled || actor.doomed || actor.triggerDisabled || actor.excludedContext) continue;
      if (player >= 0) playerFailures.add(player);
      if (!actorAllowed(r, actor)) continue;
      // Native distance uses the lower position, while box tests use the
      // collision center. Never infer a lower point from a visual mesh origin.
      if (!actor.lowerPosition || !actor.collisionPosition) continue;
      const lower = vec(actor.lowerPosition), collision = vec(actor.collisionPosition), d = distanceSq(r.secondaryPosition, lower);
      if (point(lower, collision) || ((r.configFlags & 8192) && d < r.triggerRadius ** 2 &&
          (!box || obstaclePositionInBox(r, collision)))) playerFailures.delete(player);
    }
    return r.configFlags & 1 ? !found : found && (!(r.configFlags & 8192) || playerFailures.size === 0);
  }
  function completion(r) {
    if (r.endReleased || !atEnd(r) || !(r.authoredState === 0 || r.authoredState === 3 || (r.configFlags & 128))) return;
    r.endReleased = true; emit('complete', r);
    if (r.blowupName) {
      emit('blowup', r, {blowupName: r.blowupName, position: r.evaluatedPosition.slice(), atAnimationObjects: !!(r.configFlags & 512)});
      r.destroyed = true; setVisibility(r, r.visible);
    }
    if (r.pickupCount && (!r.pickupsSpawned || (r.configFlags & 128))) {
      r.pickupsSpawned = true; emit('reward', r, {amount: r.pickupCount, position: r.evaluatedPosition.map((v, i) => v + r.pickupOffset[i]),
        rotationX: r.pickupDirectionX, rotationY: r.pickupDirectionY, activationRadius: r.pickupScatterHeight});
    }
    if (r.authoredState === 3 && (r.blowupName || r.pickupCount)) {jump(r, false); r.destroyed = false; setVisibility(r, r.visible);}
  }
  function update(dt, context = {}) {
    if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Obstacle dt must be in [0,1].');
    for (const r of records) {
      if (r.unsupported.length || !r.visible || r.destroyed) continue;
      if (r.enabled && !r.reverseActive) {
        if (r.externalControl) play(r, Math.sign(r.animationSpeed));
        else if (r.mode === 0 && !(r.configFlags & 2048)) {
          if (atStart(r) || r.direction < 0) play(r, 1);
        } else if ([0, 1, 2, 3, 5, 6].includes(r.mode)) {
          const blocked = r.mode === 0 || r.mode === 3;
          if (r.mode !== 3 || (r.configFlags & 2048) || r.aiActive || !atStart(r)) {
            r.proximity = !blocked && proximity(r, context);
            if (atStart(r)) {
              if (r.proximity || r.aiActive || r.triggerLatched) {play(r, 1); r.returnTimer = r.autoReturnDelay;}
            } else if (atEnd(r) && r.authoredState !== 0 && !r.proximity && !r.aiActive && !r.triggerLatched) {
              if (r.returnTimer > 0) r.returnTimer -= dt;
              else play(r, -1);
            }
          }
        }
      }
      // Already requested scene playback continues independently of the
      // gizmo's enabled bit, including reverse activation.
      if (r.direction) {
        const direction = r.direction;
        const factor = direction > 0 ? r.unknown4c : r.unknown50;
        for (const piece of r.pieces) {
          const sign = piece.endFrame >= piece.startFrame ? 1 : -1;
          const next = piece.frame + dt * 60 * piece.binding.timeFactor * Math.abs(r.animationSpeed) * factor * direction * sign;
          put(piece, Math.max(Math.min(piece.startFrame, piece.endFrame), Math.min(Math.max(piece.startFrame, piece.endFrame), next)));
        }
        if ((direction > 0 && atEnd(r)) || (direction < 0 && atStart(r))) r.direction = 0;
      }
      bounds(r);
      if (atEnd(r)) {r.triggerLatched = false; completion(r);}
      else r.endReleased = false;
    }
  }
  return {records, byId, resolve, update,
    activate(ref, on) {const r = resolve(ref); if (!r) return false; if (on && !r.enabled) jump(r, false); r.enabled = !!on; r.proximity = false;
      if (on) {r.destroyed = false; r.reverseActive = false;} return true;},
    activateReverse(ref, on, flags = 0) {
      const r = resolve(ref); if (!r || r.unsupported.length) return false;
      if (flags & 1) {
        if (!!on === r.reverseActive && (!(flags & 4) || r.visible !== !!on)) return false;
        return !(on && r.aiActive);
      }
      r.reverseActive = !!on; r.enabled = !on; if (on) play(r, -1); return true;
    },
    setVisibility(ref, on) {const r = resolve(ref); if (!r) return false; setVisibility(r, on); return true;},
    getOutput(ref, index, ignoreActivation = false) {
      const r = resolve(ref); if (!r || r.unsupported.length) return null;
      if (!ignoreActivation && (!r.enabled || !r.visible)) return false;
      if (index === 0) return atEnd(r); if (index === 1) return !atStart(r);
      if (index === 2) return !!r.proximity; if (index === 3) return atStart(r); if (index === 4) return r.direction > 0 && !atEnd(r);
      return null;
    },
    hit(ref) {
      const r = resolve(ref); if (!r || !r.enabled || !r.visible || r.destroyed || r.unsupported.length) return false;
      if (r.configFlags & 2048) r.triggerLatched = true;
      else {
        if (!(r.configFlags & 4096)) return false;
        if (r.blowupName) emit('blowup', r, {blowupName: r.blowupName, position: r.evaluatedPosition.slice(), atAnimationObjects: !!(r.configFlags & 512)});
        jump(r, true); r.destroyed = true; setVisibility(r, r.visible); emit('destroyed', r);
      }
      return true;
    },
    setExternalControl(ref, speed) {const r = resolve(ref); if (!r || !Number.isFinite(speed)) return false; r.externalControl = true; r.animationSpeed = speed; return true;},
    setAIActive(ref, on) {const r = resolve(ref); if (!r) return false; r.aiActive = !!on; return true;},
    jumpToStart(ref) {const r = resolve(ref); if (!r) return false; jump(r, false); return true;},
    jumpToEnd(ref) {const r = resolve(ref); if (!r) return false; jump(r, true); return true;},
    hitTargets() {return records.filter(r => r.enabled && r.visible && !r.destroyed && !r.unsupported.length && !r.boundsUnsupported &&
      r.radius > 0 && !atEnd(r) && (r.configFlags & (2048 | 4096))).map(r => ({id: r.id, name: r.name, kind: 'obstacle',
        position: {x: r.evaluatedPosition[0], y: r.evaluatedPosition[1], z: r.evaluatedPosition[2]}, radius: r.radius, yRadius: r.radius, eligible: true, record: r}));},
    getDrawStates() {return records.flatMap(r => r.pieces.map(p => ({id: r.id, name: p.name, matrix: p.matrix.slice(), frame: p.frame, visible: r.visible && !r.destroyed})));},
    drainEvents() {return events.splice(0);},
    limitations: ['repeating state2 animation intervals', 'push-only object motion supplied externally',
      'shove response', 'audio/debris/reward rendering supplied by event consumer']};
}
